import { inject, Injectable } from '@angular/core';
import { FormGroup, ValidationErrors } from '@angular/forms';
import { ApiException, isUnityValidationProblem, UnityValidationError } from '@unity/clients';
import { catchError, EMPTY, Observable, take } from 'rxjs';
import { ToasterService } from './toaster.service';

@Injectable({
  providedIn: 'root',
})
export class NewFormService {
  private readonly toasterService = inject(ToasterService);

  private handleInvalidForm(formGroup: FormGroup) {
    const errors: Record<string, ValidationErrors> = {};
    if (formGroup.errors) {
      errors['Form'] = formGroup.errors;
    }
    for (const [key, control] of Object.entries(formGroup.controls)) {
      if (control.errors) {
        errors[key] = control.errors;
      }
    }
    const messages = JSON.stringify(errors);
    this.toasterService.showError('Form invalid', new Error(messages), 'warning');
    formGroup.markAllAsTouched();
  }

  public handleServerValidationErrors(formGroup: FormGroup, errors: Record<string, UnityValidationError[]>) {
    if (errors['']) {
      this.toasterService.showError('Form invalid', new Error(errors[''].join('. ')), 'warning');
    }

    formGroup.enable();
    for (const key of Object.keys(errors)) {
      const control = formGroup.get(key);
      const errorMessage = errors[key].map(x => x.message).join('. ');
      if (control) {
        control.setErrors({
          serverError: errorMessage,
        });
        this.toasterService.showError('Form invalid', new Error(errorMessage), 'warning');
      }
    }
    formGroup.updateValueAndValidity();
  }

  public createResource$<TResponseDto>(formGroup: FormGroup, createResourceFn$: (formGroup: FormGroup) => Observable<TResponseDto>) {
    formGroup.updateValueAndValidity();

    if (formGroup.valid) {
      return createResourceFn$(formGroup).pipe(
        catchError(e => {
          if (ApiException.isApiException(e) && isUnityValidationProblem(e.result)) {
            this.handleServerValidationErrors(formGroup, e.result.errors ?? {});
          } else {
            this.toasterService.showError('Error while saving changes', e, 'error');
            formGroup.reset();
            formGroup.updateValueAndValidity();
          }
          return EMPTY;
        }),
        take(1)
      );
    } else {
      this.handleInvalidForm(formGroup);
      return EMPTY;
    }
  }
}
